import React, { type FC } from "react";
import { TaskProgressControl } from "@/components/ui/task-steps-progress/TaskProgressControl";

export interface TaskProgressBarProps {
  progress: number;
  fillClassName: string;
  showStop?: boolean;
  showResume?: boolean;
  onStop?: () => void;
  onResume?: () => void;
  stopPending?: boolean;
  resumePending?: boolean;
  /** Optional label after the control (e.g. Task Manager table). */
  percentLabel?: string;
  testId?: string;
}

/** Progress track with optional stop/resume control on the right. */
export const TaskProgressBar: FC<TaskProgressBarProps> = ({
  progress,
  fillClassName,
  showStop = false,
  showResume = false,
  onStop,
  onResume,
  stopPending = false,
  resumePending = false,
  percentLabel,
  testId = "task-progress-bar",
}) => {
  const pct = Math.min(100, Math.max(0, progress));
  const showControl = (showStop && onStop) || (showResume && onResume);
  const controlMode = showResume ? "resume" : "stop";
  const controlHandler = showResume ? onResume : onStop;
  const controlDisabled = showResume ? resumePending : stopPending;

  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testId}>
      <div className="flex-1 min-w-0 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${fillClassName}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {showControl && controlHandler && (
        <TaskProgressControl
          mode={controlMode}
          onClick={controlHandler}
          disabled={controlDisabled}
          testId={`${testId}-control`}
        />
      )}
      {percentLabel != null && (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[40px] tabular-nums">
          {percentLabel}
        </span>
      )}
    </div>
  );
};
